import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ChannelService } from '../../../../../services/channel.service';
import { ChannelResponsibleEmployee, IChannel, IChannelGroup } from '../../../../../model/channel.model';
import { SelectType } from '../../../../select/select.interface';
import { trimValidator } from '../../../../../helpers';
import { CHANNEL_TYPES_OBJECT } from '../../../../../enum/constants';

type ChannelOption = IChannel & { img: string };

@Component({
  selector: 'app-channel-group-form',
  templateUrl: './channel-group-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full' },
})
export class ChannelGroupFormComponent implements OnInit {
  @Input() group: IChannelGroup | null = null;
  @Output() close = new EventEmitter<IChannelGroup | void>();

  private readonly service = inject(ChannelService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly SelectType = SelectType;
  protected channels: ChannelOption[] = [];
  protected employees: ChannelResponsibleEmployee[] = [];
  protected loading = true;
  protected saving = false;
  protected loadError = false;
  protected saveError = false;
  protected readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, trimValidator] }),
    channel_ids: new FormControl<string[]>([], { nonNullable: true }),
    responsible_ids: new FormControl<string[]>([], { nonNullable: true }),
    distribute_chats: new FormControl(false, { nonNullable: true }),
    distribution_user_ids: new FormControl<string[]>([], { nonNullable: true }),
  });

  protected get availableChannels(): ChannelOption[] {
    return this.channels.filter((channel) => !channel.channel_group_id || channel.channel_group_id === this.group?.id);
  }

  protected get responsibleOptions(): ChannelResponsibleEmployee[] {
    const selected = this.form.controls.responsible_ids.value;
    return this.employees.filter((employee) => !employee.disabled || selected.includes(employee.id));
  }

  protected get distributionOptions(): ChannelResponsibleEmployee[] {
    const responsible = this.form.controls.responsible_ids.value;
    return this.responsibleOptions.filter((employee) => responsible.includes(employee.id));
  }

  ngOnInit(): void {
    this.form.reset({
      name: this.group?.name ?? '',
      channel_ids: [...(this.group?.channel_ids ?? [])],
      responsible_ids: [...(this.group?.responsible_ids ?? [])],
      distribute_chats: this.group?.distribute_chats ?? false,
      distribution_user_ids: [...(this.group?.distribution_user_ids ?? [])],
    });
    this.form.controls.responsible_ids.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((responsibleIds) => {
        const control = this.form.controls.distribution_user_ids;
        control.setValue(control.value.filter((id) => responsibleIds.includes(id)), { emitEvent: false });
      });
    this.load();
  }

  public requestClose(): void {
    if (!this.saving) this.close.emit();
  }

  protected load(): void {
    this.loading = true;
    this.loadError = false;
    forkJoin({ channels: this.service.getChannels(), employees: this.service.getResponsibleEmployees() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ channels, employees }) => {
          this.channels = channels.map((channel: IChannel) => {
            const img = CHANNEL_TYPES_OBJECT[channel.type]?.img_url;
            return { ...channel, img: img && img !== 'UNKNOWN' ? img : '' };
          });
          this.employees = employees;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
        },
      });
  }

  protected save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.loading || this.loadError || this.saving) return;

    this.saving = true;
    this.saveError = false;
    const body = this.form.getRawValue();
    this.form.disable();
    this.service.saveChannelGroup(this.group?.id ?? null, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => this.close.emit(saved),
        error: () => {
          this.saving = false;
          this.saveError = true;
          this.form.enable();
        },
      });
  }
}
